import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Search, Trash2, Building, MapPin, CheckCircle, XCircle, Clock, AlertTriangle, Edit, X, Image as ImageIcon, Briefcase, RefreshCw, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import ImageGallery from '../components/ImageGallery';

const Properties = () => {
  const { colors } = useTheme();
  const { currentUser } = useAuth();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterApprovalStatus, setFilterApprovalStatus] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryProperty, setGalleryProperty] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    filterProperties();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, searchTerm, filterType, filterApprovalStatus]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const propertiesQuery = query(collection(db, 'properties'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(propertiesQuery);
      const propertiesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProperties(propertiesData);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };

  const filterProperties = () => {
    let filtered = properties;

    if (searchTerm) {
      filtered = filtered.filter(property =>
        (property.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (property.titleArabic || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (property.address?.city || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(property => property.type === filterType);
    }

    if (filterApprovalStatus !== 'all') {
      filtered = filtered.filter(property => {
        const status = property.approvalStatus || 'pending';
        return status === filterApprovalStatus;
      });
    }

    setFilteredProperties(filtered);
    setCurrentPage(1); // Reset to first page
  };

  const openStatusModal = (property) => {
    setSelectedProperty(property);
    setNewStatus(property.approvalStatus || 'pending');
    setRejectionReason(property.rejectionReason || '');
    setShowStatusModal(true);
  };

  const openImageGallery = async (property) => {
    try {
      const getPropertyDocumentAccess = httpsCallable(functions, 'getPropertyDocumentAccess');
      const result = await getPropertyDocumentAccess({ propertyId: property.id });
      setGalleryProperty({
        ...property,
        verificationDocUrls: result.data?.verificationDocs || [],
        subleaseConsentDocUrl: result.data?.subleaseConsentDoc || null,
      });
    } catch (error) {
      console.error('Error fetching secure property document URLs:', error);
      setGalleryProperty({
        ...property,
        verificationDocUrls: [],
        subleaseConsentDocUrl: null,
      });
      toast.error('Could not load secure property documents.');
    }
    setShowImageGallery(true);
  };

  const closeImageGallery = () => {
    setShowImageGallery(false);
    setGalleryProperty(null);
  };

  const updatePropertyStatus = async () => {
    if (!selectedProperty || !newStatus) return;

    try {
      const propertyRef = doc(db, 'properties', selectedProperty.id);
      const updateData = {
        approvalStatus: newStatus,
        updatedAt: new Date()
      };

      if (newStatus === 'approved') {
        updateData.approvedAt = new Date();
        updateData.approvedBy = currentUser?.uid || 'admin';
        updateData.rejectionReason = null;
      }

      if (newStatus === 'rejected' && rejectionReason) {
        updateData.rejectionReason = rejectionReason;
      }

      if (newStatus !== 'rejected') {
        updateData.rejectionReason = null;
      }

      await updateDoc(propertyRef, updateData);

      setProperties(properties.map(property =>
        property.id === selectedProperty.id
          ? { ...property, ...updateData }
          : property
      ));

      toast.success(`Status perfectly updated to ${newStatus}`);
      setShowStatusModal(false);
      setSelectedProperty(null);
      setNewStatus('');
      setRejectionReason('');
    } catch (error) {
      console.error('Error updating property status:', error);
      toast.error('Failed to update property status');
    }
  };

  const togglePropertyAvailability = async (propertyId, currentStatus) => {
    try {
      const propertyRef = doc(db, 'properties', propertyId);
      await updateDoc(propertyRef, {
        isAvailable: !currentStatus,
        updatedAt: new Date()
      });

      setProperties(properties.map(property =>
        property.id === propertyId
          ? { ...property, isAvailable: !currentStatus }
          : property
      ));

      toast.success(`Property ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating property:', error);
      toast.error('Failed to update property');
    }
  };

  const deleteProperty = async (propertyId) => {
    if (!window.confirm('Are you strictly sure you want to delete this property? This action is permanent.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'properties', propertyId));
      setProperties(properties.filter(property => property.id !== propertyId));
      toast.success('Property deleted permanently');
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };

  const getPropertyTypeBadge = (type) => {
    const badges = {
      apartment: { bg: colors.primaryGhost, color: colors.primary, label: 'Apartment' },
      house: { bg: colors.successGhost, color: colors.success, label: 'House' },
      room: { bg: colors.warningGhost, color: colors.warning, label: 'Room' },
      villa: { bg: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', color: '#fff', label: 'Villa' }
    };

    const badge = badges[type] || badges.apartment;

    return (
      <span 
        className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider backdrop-blur-md shadow-sm"
        style={{ background: badge.bg, color: badge.color }}
      >
        {badge.label}
      </span>
    );
  };

  const getApprovalStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, icon: Clock, text: 'Pending' },
      approved: { bg: colors.successGhost, color: colors.success, icon: CheckCircle, text: 'Approved' },
      rejected: { bg: colors.errorGhost, color: colors.error, icon: XCircle, text: 'Rejected' },
      suspended: { bg: colors.backgroundSecondary, color: colors.textSecondary, icon: AlertTriangle, text: 'Suspended' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span 
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider shadow-sm backdrop-blur-md"
        style={{ backgroundColor: badge.bg, color: badge.color }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.text}
      </span>
    );
  };

  const stats = {
    total: properties.length,
    pending: properties.filter(p => (p.approvalStatus || 'pending') === 'pending').length,
    approved: properties.filter(p => p.approvalStatus === 'approved').length,
    rejected: properties.filter(p => p.approvalStatus === 'rejected').length,
    suspended: properties.filter(p => p.approvalStatus === 'suspended').length,
  };

  const kpis = [
    { id: 'all', label: 'Total Properties', value: stats.total, icon: Layers, color: colors.primary, gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' },
    { id: 'pending', label: 'Pending Review', value: stats.pending, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'approved', label: 'Approved', value: stats.approved, icon: CheckCircle, color: colors.success, gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
    { id: 'rejected', label: 'Rejected', value: stats.rejected, icon: XCircle, color: colors.error, gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' },
    { id: 'suspended', label: 'Suspended', value: stats.suspended, icon: AlertTriangle, color: colors.textMuted, gradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Properties
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Manage property listings, statuses, and featured assets.
          </p>
        </div>
        <button 
          onClick={fetchProperties}
          className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors flex items-center gap-2"
          style={{ backgroundColor: colors.surfaceHover, color: colors.text, border: `1px solid ${colors.border}` }}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh Data
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((kpi, i) => {
          const isActive = filterApprovalStatus === kpi.id;
          return (
            <div
              key={kpi.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i+1}`}
              style={{
                backgroundColor: isActive ? `${kpi.color}10` : colors.card,
                border: `1px solid ${isActive ? kpi.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${kpi.color}20` : undefined,
                ringColor: isActive ? kpi.color : 'transparent'
              }}
              onClick={() => setFilterApprovalStatus(kpi.id)}
            >
              <div className="flex flex-col h-full justify-between gap-3">
                <div className="flex items-center justify-between">
                  <div 
                    className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
                    style={{ background: kpi.gradient }}
                  >
                    <kpi.icon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{kpi.value}</p>
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: isActive ? kpi.color : colors.textSecondary }}>{kpi.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Filter Bar */}
      <div className="card-premium p-4" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]" style={{ color: colors.textMuted }} />
            <input
              type="text"
              placeholder="Search properties by title or city..."
              className="w-full pl-11 pr-4 py-2.5 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
              style={{ 
                backgroundColor: colors.backgroundSecondary,
                border: `1px solid ${colors.border}`,
                color: colors.text
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.borderActive; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          
          <div className="relative min-w-[200px]">
             <Building className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[16px] h-[16px]" style={{ color: colors.textMuted }} />
             <select
               className="w-full pl-11 pr-8 py-2.5 rounded-xl text-[13px] font-medium focus:outline-none appearance-none cursor-pointer"
               style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
               value={filterType}
               onChange={(e) => setFilterType(e.target.value)}
             >
               <option value="all">All Property Types</option>
               <option value="apartment">Apartments</option>
               <option value="house">Houses</option>
               <option value="room">Rooms</option>
               <option value="villa">Villas</option>
             </select>
          </div>
        </div>
      </div>

      {/* Properties Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 card-premium" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
          <p className="mt-4 text-sm font-medium" style={{ color: colors.textSecondary }}>Refreshing properties...</p>
        </div>
      ) : filteredProperties.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-16 card-premium" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-5" style={{ backgroundColor: colors.backgroundSecondary }}>
             <Building className="w-10 h-10" style={{ color: colors.textMuted }} />
          </div>
          <h3 className="text-[16px] font-semibold" style={{ color: colors.text }}>No properties found</h3>
          <p className="mt-1.5 text-[14px]" style={{ color: colors.textSecondary }}>
             We couldn't find any properties matching your current criteria.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredProperties.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((property, i) => (
            <div 
              key={property.id} 
              className={`card-premium overflow-hidden group hover:shadow-xl transition-all duration-300 opacity-0 animate-fadeInScale stagger-${(i%5)+1}`}
              style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
            >
              {/* Image Section */}
              <div className="relative h-56 overflow-hidden">
                {property.imageUrls && property.imageUrls.length > 0 ? (
                  <img
                    src={property.imageUrls[0]}
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: colors.backgroundSecondary }}>
                    <Building className="w-12 h-12" style={{ color: colors.textMuted }} />
                  </div>
                )}
                
                {/* Overlay Elements */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 opacity-80" />
                
                {(property.imageUrls?.length > 0 || property.verificationDocUrls?.length > 0 || property.subleaseConsentDocUrl) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openImageGallery(property); }}
                    className="absolute top-4 left-4 p-2.5 rounded-xl transition-all hover:scale-105 bg-white/10 backdrop-blur-md border border-white/20 text-white shadow-lg flex items-center justify-center group/btn"
                    title="View Property Gallery"
                  >
                    <ImageIcon className="w-[18px] h-[18px] mr-1.5" />
                    <span className="text-[12px] font-bold">
                      {(property.imageUrls?.length || 0) + (property.verificationDocUrls?.length || 0) + (property.subleaseConsentDocUrl ? 1 : 0)} All
                    </span>
                  </button>
                )}
                
                <div className="absolute top-4 right-4">
                  {getApprovalStatusBadge(property.approvalStatus || 'pending')}
                </div>
                <div className="absolute bottom-4 left-4">
                  {getPropertyTypeBadge(property.type)}
                </div>
                 <div className="absolute bottom-4 right-4 text-white font-bold text-lg drop-shadow-md">
                    {property.monthlyRent?.toLocaleString() || '0'} <span className="text-xs text-white/80">SAR/mo</span>
                 </div>
              </div>
              
              {/* Content Section */}
              <div className="p-5">
                <div className="flex justify-between items-start gap-3">
                   <div className="flex-1 min-w-0">
                     <h3 className="text-[16px] font-bold truncate transition-colors group-hover:text-primary" style={{ color: colors.text }}>
                       {property.title || 'Untitled Property'}
                     </h3>
                     <div className="flex items-center mt-1.5 opacity-80" style={{ color: colors.textSecondary }}>
                       <MapPin className="w-3.5 h-3.5 mr-1" />
                       <span className="text-[12px] font-medium truncate">
                         {property.address?.city || 'Unknown'}, {property.address?.district || 'Saudi Arabia'}
                       </span>
                     </div>
                   </div>
                   <div 
                     className="px-2 py-1 rounded-md text-[10px] font-bold uppercase"
                     style={{ 
                       backgroundColor: property.isAvailable ? colors.successGhost : colors.errorGhost,
                       color: property.isAvailable ? colors.success : colors.error,
                       border: `1px solid ${property.isAvailable ? colors.success : colors.error}30`
                     }}
                   >
                     {property.isAvailable ? 'LIVE' : 'HIDDEN'}
                   </div>
                </div>
                
                <div className="flex items-center gap-4 mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
                  <div className="flex items-center gap-1.5" style={{ color: colors.textSecondary }}>
                     <Briefcase className="w-3.5 h-3.5" />
                     <span className="text-[12px] font-semibold">{property.bedrooms || 0} Bed</span>
                  </div>
                  <div className="flex items-center gap-1.5" style={{ color: colors.textSecondary }}>
                     <Briefcase className="w-3.5 h-3.5" />
                     <span className="text-[12px] font-semibold">{property.bathrooms || 0} Bath</span>
                  </div>
                  <div className="flex items-center gap-1.5" style={{ color: colors.textSecondary }}>
                     <Briefcase className="w-3.5 h-3.5" />
                     <span className="text-[12px] font-semibold">Max {property.maxOccupants || 0}</span>
                  </div>
                </div>

                {property.rejectionReason && property.approvalStatus === 'rejected' && (
                  <div className="mt-3 p-2.5 rounded-lg text-[12px] font-medium" style={{ backgroundColor: colors.errorGhost, color: colors.error, border: `1px solid ${colors.error}20` }}>
                    <span className="font-bold">Reason:</span> {property.rejectionReason}
                  </div>
                )}
                
                {/* Actions Grid */}
                <div className="flex gap-2.5 mt-5">
                  <button
                    onClick={() => openStatusModal(property)}
                    className="flex-1 py-2 rounded-xl text-[12px] font-bold transition-transform hover:-translate-y-0.5 flex items-center justify-center shadow-sm"
                    style={{ 
                      backgroundColor: colors.primary,
                      color: '#fff'
                    }}
                  >
                    <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit Status
                  </button>
                  
                  <button
                    onClick={() => togglePropertyAvailability(property.id, property.isAvailable)}
                    className="p-2 w-10 flex items-center justify-center rounded-xl transition-transform hover:-translate-y-0.5"
                    style={{ 
                      backgroundColor: property.isAvailable ? colors.warningGhost : colors.successGhost,
                      color: property.isAvailable ? colors.warning : colors.success,
                      border: `1px solid ${property.isAvailable ? colors.warning : colors.success}40`
                    }}
                    title={property.isAvailable ? 'Hide Property' : 'Publish Property'}
                  >
                    {property.isAvailable ? <AlertTriangle className="w-[18px] h-[18px]" /> : <CheckCircle className="w-[18px] h-[18px]" />}
                  </button>
                  
                  <button
                    onClick={() => deleteProperty(property.id)}
                    className="p-2 w-10 flex items-center justify-center rounded-xl transition-all hover:bg-red-500 hover:text-white"
                    style={{ backgroundColor: colors.errorGhost, border: `1px solid ${colors.error}40`, color: colors.error }}
                    title="Delete Property"
                  >
                    <Trash2 className="w-[18px] h-[18px]" />
                  </button>
                </div>

              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Footer */}
      {!loading && filteredProperties.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={filteredProperties.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedProperty && (
        <div className="admin-modal-overlay fixed inset-0 flex items-center justify-center z-[60] p-4 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
          <div className="admin-modal-card admin-modal-scroll rounded-2xl max-w-lg w-full p-5 sm:p-8 shadow-2xl relative animate-fadeInScale" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            
            <div className="flex justify-between items-start mb-6">
              <div>
                 <h3 className="text-xl font-bold" style={{ color: colors.text }}>Update Property Status</h3>
                 <p className="text-[13px] mt-1" style={{ color: colors.textSecondary }}>Select a new approval status for this listing.</p>
              </div>
              <button onClick={() => { setShowStatusModal(false); setSelectedProperty(null); }} className="p-2 rounded-full hover:bg-black/5 transition-all" style={{ backgroundColor: colors.backgroundSecondary }}>
                <X className="w-5 h-5" style={{ color: colors.text }} />
              </button>
            </div>

            <div className="mb-6 p-4 rounded-xl border flex items-center justify-between" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
               <div className="min-w-0 flex-1 pr-4">
                  <p className="font-bold text-[14px] truncate" style={{ color: colors.text }}>{selectedProperty.title}</p>
                  <p className="text-[12px] truncate opacity-80" style={{ color: colors.textSecondary }}>{selectedProperty.id}</p>
               </div>
               <div>
                  {getApprovalStatusBadge(selectedProperty.approvalStatus || 'pending')}
               </div>
            </div>
            
            <div className="mb-5">
              <label className="block text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color: colors.textSecondary }}>
                Select New Status
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'pending', icon: Clock, color: colors.warning },
                  { id: 'approved', icon: CheckCircle, color: colors.success },
                  { id: 'rejected', icon: XCircle, color: colors.error },
                  { id: 'suspended', icon: AlertTriangle, color: colors.textMuted }
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setNewStatus(s.id)}
                    className={`p-3.5 rounded-xl font-bold transition-all capitalize flex items-center gap-2 ${newStatus === s.id ? 'ring-2' : 'hover:bg-opacity-80'}`}
                    style={{ 
                      backgroundColor: newStatus === s.id ? `${s.color}20` : colors.background,
                      color: newStatus === s.id ? s.color : colors.text,
                      border: `1px solid ${newStatus === s.id ? s.color : colors.border}`,
                      ringColor: s.color
                    }}
                  >
                    <s.icon className="w-[18px] h-[18px]" />
                    {s.id}
                  </button>
                ))}
              </div>
            </div>

            <div className={`transition-all duration-300 overflow-hidden ${newStatus === 'rejected' ? 'max-h-40 opacity-100 mb-6' : 'max-h-0 opacity-0 mb-0'}`}>
                <label className="block text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: colors.error }}>
                  Reason for Rejection *
                </label>
                <textarea
                  className="w-full p-3.5 rounded-xl text-[14px] focus:outline-none transition-all"
                  style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                  rows={3}
                  placeholder="Explain why this property was rejected..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  onFocus={(e) => { e.target.style.borderColor = colors.error; e.target.style.boxShadow = `0 0 0 3px ${colors.errorGhost}`; }}
                  onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setShowStatusModal(false); setSelectedProperty(null); }}
                className="flex-1 py-3.5 rounded-xl font-bold transition-all"
                style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
              >
                Discard
              </button>
              <button
                onClick={updatePropertyStatus}
                disabled={newStatus === 'rejected' && (!rejectionReason || !rejectionReason.trim())}
                className="flex-1 py-3.5 rounded-xl font-bold text-white transition-all hover:-translate-y-0.5 shadow-md disabled:opacity-50 disabled:hover:translate-y-0"
                style={{ background: newStatus === 'rejected' ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' : newStatus === 'approved' ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' : 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' }}
              >
                Apply Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {showImageGallery && galleryProperty && (
        <ImageGallery
          propertyImages={galleryProperty.imageUrls || []}
          verificationDocs={galleryProperty.verificationDocUrls || []}
          subleaseConsentDoc={galleryProperty.subleaseConsentDocUrl}
          propertyTitle={galleryProperty.title || 'Property Images'}
          onClose={closeImageGallery}
        />
      )}
    </div>
  );
};

export default Properties;
